import { ComponentProps } from "react";
import { IComponentParent } from "sonobello.utilities.react";

import { ProtectedAppContextProvider } from "../../AppContext";
import NoConfirmationNewPatientFlow, {
  PreQualifiedNoConfirmationNewPatientFlow
} from "../../Flows/compositions/NoConfirmationNewPatientFlow";
import NoConfirmationNoBookFlow, {
  PreQualifiedNoConfirmationNoBookFlow
} from "../../Flows/compositions/NoConfirmationNoBookFlow";
import NoConfirmationRebookFlow, {
  PreQualifiedNoConfirmationRebookFlow
} from "../../Flows/compositions/NoConfirmationRebookFlow";
import SingleMedicalStepNewPatientFlow, {
  PreQualifiedSingleMedicalStepNewPatientFlow
} from "../../Flows/compositions/SingleMedicalStepNewPatientFlow";
import SingleMedicalStepNoBookFlow, {
  PreQualifiedSingleMedicalStepNoBookFlow
} from "../../Flows/compositions/SingleMedicalStepNoBookFlow";
import SingleMedicalStepRebookFlow, {
  PreQualifiedSingleMedicalStepRebookFlow
} from "../../Flows/compositions/SingleMedicalStepRebookFlow";
import FailoverFlowResolver from "../../Flows/types/FailoverFlowResolver";
import NameMatchFlowResolver from "../../Flows/types/NameMatchFlowResolver";
import PatientQualificationFlowResolver from "../../Flows/types/PatientQualificationFlowResolver";
import SingleMedicalStepNewPatientFlowResolver from "../../Flows/types/SingleMedicalStepNewPatientFlowResolver";
import SingleMedicalStepNoBookFlowResolver from "../../Flows/types/SingleMedicalStepNoBookFlowResolver";
import SingleMedicalStepRebookFlowResolver from "../../Flows/types/SingleMedicalStepRebookFlowResolver";
import EnvironmentConfiguration from "../../types/EnvironmentConfiguration";
import ExperimentAssignmentAppContextProvider, {
  IExperimentAssignmentAppContextProviderProps,
  CreatePatientExperimentHook
} from "../Components/ExperimentAssignmentAppContextProvider";
import useCreatePatientExperimentManualAuth from "../hooks/useCreatePatientExperiment";
import ExperimentContextValueProvider from "./ExperimentContextValueProvider";

const useCreatePatientExperimentDisabledIfExperimentForced: CreatePatientExperimentHook =
  EnvironmentConfiguration.forceExperimentalFlowVariant
    ? props => ({
        result: null,
        error: null,
        isLoading: false,
        execute: () => {
          // no-op: hook is disabled when forced variant is enabled
        }
      })
    : useCreatePatientExperimentManualAuth;

/**
 * @remarks
 * Resolver decision tree (highest priority first):
 * 1) Pre-qualified branch (when patient is pre-qualified):
 *    a) Directly match explicit treatment flow names (`PreQualifiedSingleMedicalStep*`) with no experiment lookup.
 *    b) For control-family names (`newPatient`/`rebook`/`noBook` and legacy aliases), evaluate the
 *       `SingleMedicalStepFlow` experiment and choose treatment vs control flow.
 *    c) Fallback to pre-qualified legacy control flows.
 * 2) Standard branch (all other patients):
 *    a) Directly match explicit treatment flow names (`SingleMedicalStep*`) with no experiment lookup.
 *    b) For control-family names (`newPatient`/`rebook`/`noBook` and legacy aliases), evaluate the
 *       `SingleMedicalStepFlow` experiment and choose treatment vs control flow.
 *    c) Fallback to legacy control flows.
 */
const configuredExperimentAssignmentAppContextProvider: React.FC<
  IExperimentAssignmentAppContextProviderProps
> = props =>
  ExperimentAssignmentAppContextProvider({
    ...props,
    flowResolver: new FailoverFlowResolver([
      new PatientQualificationFlowResolver(
        props.session.patient.isPreviouslyQualified && !EnvironmentConfiguration.forcePatientUnqualified,
        new FailoverFlowResolver([
          new NameMatchFlowResolver([
            new PreQualifiedSingleMedicalStepNewPatientFlow(),
            new PreQualifiedSingleMedicalStepRebookFlow(),
            new PreQualifiedSingleMedicalStepNoBookFlow()
          ]),
          new SingleMedicalStepNewPatientFlowResolver(
            new PreQualifiedSingleMedicalStepNewPatientFlow(),
            new PreQualifiedNoConfirmationNewPatientFlow()
          ),
          new SingleMedicalStepRebookFlowResolver(
            new PreQualifiedSingleMedicalStepRebookFlow(),
            new PreQualifiedNoConfirmationRebookFlow()
          ),
          new SingleMedicalStepNoBookFlowResolver(
            new PreQualifiedSingleMedicalStepNoBookFlow(),
            new PreQualifiedNoConfirmationNoBookFlow()
          ),
          new NameMatchFlowResolver([
            new PreQualifiedNoConfirmationNewPatientFlow(),
            new PreQualifiedNoConfirmationRebookFlow(),
            new PreQualifiedNoConfirmationNoBookFlow()
          ])
        ])
      ),
      new FailoverFlowResolver([
        new NameMatchFlowResolver([
          new SingleMedicalStepNewPatientFlow(),
          new SingleMedicalStepRebookFlow(),
          new SingleMedicalStepNoBookFlow()
        ]),
        new SingleMedicalStepNewPatientFlowResolver(
          new SingleMedicalStepNewPatientFlow(),
          new NoConfirmationNewPatientFlow()
        ),
        new SingleMedicalStepRebookFlowResolver(new SingleMedicalStepRebookFlow(), new NoConfirmationRebookFlow()),
        new SingleMedicalStepNoBookFlowResolver(new SingleMedicalStepNoBookFlow(), new NoConfirmationNoBookFlow()),
        new NameMatchFlowResolver([
          new NoConfirmationNewPatientFlow(),
          new NoConfirmationRebookFlow(),
          new NoConfirmationNoBookFlow()
        ])
      ])
    ]),
    useCreatePatientExperiment: useCreatePatientExperimentDisabledIfExperimentForced,
    AppContext: ProtectedAppContextProvider
  });

const ExperimentContextAppContextProvider: React.FC<
  IComponentParent & Omit<IExperimentAssignmentAppContextProviderProps, "experimentContextProps">
> = ExperimentContextValueProvider<
  Pick<IExperimentAssignmentAppContextProviderProps, "experimentContextProps">,
  ComponentProps<typeof configuredExperimentAssignmentAppContextProvider>
>({
  Component: configuredExperimentAssignmentAppContextProvider,
  map: experimentContextProps => ({ experimentContextProps })
});

export default ExperimentContextAppContextProvider;
